import { useEffect, useMemo, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { setScanPageHandler } from "@/hooks/useGlobalScanner";
import { TRY, NUM, pname, sameOrNew } from "@/lib/format";
import { quickSaleSummary } from "@/components/QuickSaleHistoryList";
import { cn } from "@/lib/utils";
import { discountFor } from "@/lib/discountKey";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { contactOptions as contactOptionsOf } from "@/lib/contacts";
import { RecordHistory } from "@/components/RecordHistory";
import { Zap, Plus, ShoppingCart, Boxes, History, Sigma, Wallet, ArrowRight, ScanLine, Users as UsersIcon } from "lucide-react";
import BarcodeScanner from "@/components/BarcodeScanner";
import QnbInvoiceDialog from "@/components/QnbInvoiceDialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";
import { usePosCart } from "@/hooks/usePosCart";
import { LiveCartsPanel } from "@/components/pos/LiveCartsPanel";
import { PendingSalesBar } from "@/components/pos/PendingSalesBar";
import { HeroKpi } from "@/components/pos/PosBits";
import { PayDialog } from "@/components/pos/PayDialog";
import { ReceiptPreviewDialog } from "@/components/pos/ReceiptPreviewDialog";
import { SendToPrepDialog } from "@/components/pos/SendToPrepDialog";
import { SalesHistoryPanel } from "@/components/pos/SalesHistoryPanel";
import { ZReportDialog } from "@/components/pos/ZReportDialog";
import { EditSaleDialog } from "@/components/pos/EditSaleDialog";
import { CariTransferDialog } from "@/components/pos/CariTransferDialog";
import { NewProductDialog } from "@/components/pos/NewProductDialog";
import { InvoiceFromSaleDialog } from "@/components/pos/InvoiceFromSaleDialog";
import { CartInvoiceDialog } from "@/components/pos/cartInvoice/CartInvoiceDialog";
import InvoiceDetailDialog from "@/components/InvoiceDetailDialog";
import { ReturnDialog } from "@/components/pos/ReturnDialog";
import { StaffTodayCard } from "@/components/pos/StaffTodayCard";
import { ChangeModeDialog } from "@/components/pos/ChangeModeDialog";
import { PosTerminal } from "@/components/pos/terminal/PosTerminal";
import { ConvertToQuoteDialog } from "@/components/pos/ConvertToQuoteDialog";
import { KDV_DEFAULT, r2, PAY_LABEL, MODES, paymentsSummary } from "@/lib/pos";
import { receiptBelgeNo, unitGrossOf } from "@/lib/receipt";
import { playNotifySound } from "@/lib/sound";

// pos yoksa (yeni ürün vb.) tek iskontodan 3 mod türet
const buildPos = (p, disc) => {
  const price = p.price || 0;
  const kdv = p.kdv_rate != null ? p.kdv_rate : KDV_DEFAULT;
  const net = Math.round(price * (1 - (disc || 0) / 100) * 100) / 100;
  const gross = Math.round(net * (1 + kdv / 100) * 100) / 100;
  return {
    genel: { unit_net: net, unit_gross: gross, sale_discount: disc || 0, kdv_rate: kdv, vat: true },
    faturali: { unit_net: net, unit_gross: gross, sale_discount: disc || 0, kdv_rate: kdv, vat: true },
    kdvsiz: { unit_net: net, unit_gross: net, sale_discount: disc || 0, kdv_rate: 0, vat: false },
  };
};
const modeGross = (m) => (m.unit_gross != null ? r2(m.unit_gross) : r2((m.unit_net || 0) * (1 + (m.kdv_rate || 0) / 100)));
const fetchSale = async (s) => { try { const { data } = await api.get(`/quick-sales/${s.id}`); return data; } catch { return s; } };

export default function QuickSale() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [discMap, setDiscMap] = useState({});
  const [history, setHistory] = useState([]);
  const [staffToday, setStaffToday] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [open, setOpen] = useState(false);
  const openRef = useRef(false); openRef.current = open;
  // Canlı + çevrimdışı dayanıklı sepet (sunucuda yaşar, cihazda saklanır, birden çok kullanıcı aynı anda düzenler)
  const pc = usePosCart({ user, onClosedElsewhere: () => { if (openRef.current) { setOpen(false); setPayOpen(false); setCartInvOpen(false); } loadHistory(); } });
  const cart = pc.items;
  const mode = pc.mode;
  const title = pc.title;
  const note = pc.note;
  const payMethod = pc.payMethod;
  // Depo müdürü: yalnız Genel + Elektrikçi Faturalı; yönetici sepetteyse (sahip / son 10 dk'da dokunan) fiyat-iskonto gizli, satış kilitli
  const restricted = !isAdmin && pc.adminPresent;
  const workerLocked = !isAdmin && (restricted || mode === "kdvsiz");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [facets, setFacets] = useState({ brands: [], categories: [], discount_groups: [], groups_by_brand: {} });
  const [filters, setFilters] = useState({ brand: "", series: "", category: "" });
  const [scanOpen, setScanOpen] = useState(false);
  const [leftTab, setLeftTab] = useState("search");  // sol panel: ürün ara | cari geçmişi
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);
  const searchSeq = useRef(0);   // her aramaya sıra no → geç dönen ESKİ sonuç güncel listeyi ezmesin

  // dialogs
  const [payOpen, setPayOpen] = useState(false);
  const [payOverride, setPayOverride] = useState("");
  const [edit, setEdit] = useState(null);
  const [cari, setCari] = useState(null);
  const [histSale, setHistSale] = useState(null);
  const [newProd, setNewProd] = useState(null);       // { name, barcode }
  const [preview, setPreview] = useState(null);       // tam satış kaydı
  const [prepSale, setPrepSale] = useState(null);     // hazırlanacak listeye gönderilecek fiş
  const [quoteSale, setQuoteSale] = useState(null);   // teklife çevrilecek fiş
  const [invoiceSale, setInvoiceSale] = useState(null);   // fatura kesme kalem/alıcı seçimi
  const [qnbInv, setQnbInv] = useState(null);             // oluşturulan taslak fatura → QNB portalı
  const [cartInvOpen, setCartInvOpen] = useState(false);  // sepet açıkken fatura kesme ekranı
  const [cartInvPending, setCartInvPending] = useState(null);   // kesildi ama satış tamamlanamadı → tekrar dene
  const [qnbExtra, setQnbExtra] = useState([]);           // fatura ekranında yeni açılan / güncellenen cari
  const cartInvRef = useRef(null);                        // { invoiceId, invoiceNo, method, total }
  const [activeIdx, setActiveIdx] = useState(0);   // arama sonuçlarında klavye ile seçim
  const [showProfit, setShowProfit] = useState(false);   // yönetici kâr paneli (varsayılan gizli)
  const [zOpen, setZOpen] = useState(false);
  const [ret, setRet] = useState(null);
  const [detailInv, setDetailInv] = useState(null);
  const [modeSale, setModeSale] = useState(null);       // satış modunu sonradan değiştir
  const viewInvoice = async (s) => { if (!s.invoice_id) return; try { const { data } = await api.get(`/invoices/${s.invoice_id}`); setDetailInv(data); } catch (e) { toast.error(apiError(e)); } };

  const loadHistory = () => {
    if (!isAdmin) return;   // depo müdürü yapılan fişleri/satışları göremez — geçmiş hiç çekilmez
    api.get("/quick-sales").then((r) => setHistory(sameOrNew(r.data))).catch(() => {});
    api.get("/quick-sales/z-report").then((r) => setStaffToday(r.data.by_user || [])).catch(() => {});
  };
  const loadContacts = () => api.get("/contacts").then((r) => setContacts(r.data || [])).catch(() => {});
  useEffect(() => {
    api.get("/discounts/resolve").then((r) => setDiscMap(r.data.map || {})).catch(() => {});
    if (isAdmin) loadContacts();
    api.get("/products/facets").then((r) => setFacets(r.data || {})).catch(() => {});
    loadHistory();
  }, []); // eslint-disable-line
  // Satış ekranı açıkken geçmiş görünmez → her değişiklikte 1 MB'lık listeyi çekme (kasada yavaşlama); kapanınca bir kez yenilenir
  const histStale = useRef(false);
  useAutoRefresh(() => { if (openRef.current) histStale.current = true; else loadHistory(); });
  useEffect(() => { if (!open && histStale.current) { histStale.current = false; loadHistory(); } }, [open]); // eslint-disable-line

  // Depo müdürü: yönetici sepete katıldığı AN (fiyatlar gizlendi) → ses + uyarı + zil bildirimi yenilenir
  const adminSeen = useRef({ cartId: null, present: false });
  useEffect(() => {
    const prev = adminSeen.current;
    const cur = { cartId: pc.cartId, present: !!pc.adminPresent };
    adminSeen.current = cur;
    if (isAdmin || !cur.cartId || prev.cartId !== cur.cartId || prev.present || !cur.present) return;
    const adm = (pc.editors || []).find((e) => e.role === "admin" && e.id !== user?.id);
    playNotifySound();
    toast.warning(`${adm?.name || "Yönetici"} sepete katıldı — fiyatlar gizlendi`, { description: "Satışı yönetici tamamlayacak. Ürün ekleyip adet düzenlemeye devam edebilirsiniz.", duration: 10000 });
    window.dispatchEvent(new CustomEvent("ge:notifications-poll", { detail: { silentType: "pos_sepet" } }));
  }, [pc.cartId, pc.adminPresent, pc.editors]); // eslint-disable-line

  const discOf = (p) => discountFor(p, discMap) || 0;

  const runSearch = (term, f = filters) => {
    if (timer.current) clearTimeout(timer.current);
    const q = (term ?? "").trim();
    const hasFilter = f.brand || f.series || f.category;
    if (!q && !hasFilter) { setResults([]); return; }
    setSearching(true);
    const seq = ++searchSeq.current;
    timer.current = setTimeout(async () => {
      try {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (f.brand) p.set("brand", f.brand);
        if (f.series) p.set("series", f.series);
        if (f.category) p.set("category", f.category);
        p.set("lite", "true");   // hız: stok hareketi/sipariş join'i olmadan → marka listesi anında gelir
        const { data } = await api.get(`/products/lookup?${p.toString()}`);
        if (seq === searchSeq.current) setResults(data);   // yalnız EN SON aramanın sonucu geçerli
      } catch (e) { toast.error(apiError(e)); }
      finally { if (seq === searchSeq.current) setSearching(false); }
    }, 220);
  };
  const setFilter = (patch) => {
    const nf = { ...filters, ...patch };
    if (patch.brand !== undefined) { nf.series = ""; nf.category = ""; }
    setFilters(nf);
    runSearch(query, nf);
  };
  const clearFilters = () => {
    const nf = { brand: "", series: "", category: "" };
    setFilters(nf);
    runSearch(query, nf);
  };

  const lastScan = useRef({ id: "", t: 0 });
  const addProduct = (p, disc = discOf(p), r = null, fromScan = false) => {
    // Barkod taramada çift-ekleme koruması: aynı ürün 350ms içinde tekrar taranırsa (yarış) 2. eklemeyi yok say.
    if (fromScan) {
      const now = Date.now();
      if (lastScan.current.id === p.id && now - lastScan.current.t < 350) { setQuery(""); setResults([]); return; }
      lastScan.current = { id: p.id, t: now };
    }
    // Bekleyen/eski aramayı iptal et → ürün eklenince güncel olmayan sonuçlar listeye geri dolmasın
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    searchSeq.current++;
    const pos = (r && r.pos) || buildPos(p, disc);
    const m = pos[mode] || pos.genel;
    const drums = ((p.drums || (r && r.product && r.product.drums) || []) || []).filter((d) => (Number(d.remaining) || 0) > 0);
    // sepette varsa miktar +1 ve en üste; yoksa yeni kalem en üste (sunucu da aynı kuralı uygular)
    pc.dispatch({ op: "add", item: {
      product_id: p.id, name: pname(p), code: p.code || "", barcode: p.barcode || "",
      image_url: p.image_url || (p.images || [])[0] || "", stock: p.quantity || 0,
      unit: p.unit || "ADET", brand: p.brand || p.discount_group || "",
      quantity: 1, list_price: p.price || 0, unit_net: m.unit_net, gross: String(modeGross(m)), manual_price: !!m.manual, sale_discount: m.sale_discount, kdv_rate: m.kdv_rate,
      cost: (p.cost != null ? p.cost : (r && r.product ? r.product.cost : 0)) || 0,
      cost_source: (r && r.product ? r.product.cost_source : p.cost_source) || "auto",
      pos, is_cable: !!(r && r.is_cable), drums, drum_id: drums.length === 1 ? drums[0].id : "",
    } });
    setQuery("");
    if (filters.brand || filters.series || filters.category) runSearch("", filters);
    else setResults([]);
    toast.success(`${pname(p)} eklendi`);
  };

  // mod değişince sepetteki her kalemin iskonto + KDV'si yeniden hesaplanır (sunucu + tüm ekranlar aynı anda)
  const changeMode = (m) => pc.setMode(m);

  const handleScan = async (code, fromBarcode = true) => {
    setScanOpen(false);
    try {
      const { data } = await api.get(`/products/lookup?q=${encodeURIComponent(code)}`);
      const exact = data.find((r) => (r.product.barcode || "") === code || (r.product.code || "") === code || (r.product.barcodes || []).includes(code));
      if (exact) { addProduct(exact.product, exact.sale_discount, exact, true); return; }
      if (fromBarcode) {
        // Barkod okundu ama sistemde YOK → uyar + arama kutusunu TEMİZLE (sonraki taramada metin birikmesin)
        setQuery(""); setResults([]);
        toast.error(`Barkod sistemde YOK: ${code}`, { duration: 10000, action: { label: "Yeni ürün ekle", onClick: () => setNewProd({ name: "", barcode: String(code || "").trim() }) } });
        return;
      }
      setQuery(code); setResults(data);
      if (!data.length) toast.error("Ürün bulunamadı: " + code);
    } catch (e) { toast.error(apiError(e)); }
  };

  // Fiziksel barkod okuyucu Hızlı Satış'tayken → doğrudan sepete ekle (kataloğa gitmez). Terminal kapalıysa sepet otomatik açılır.
  const scanRef = useRef(null);
  scanRef.current = (code) => { if (!open) { startNew(); toast.info("Barkod okundu — sepet açıldı", { duration: 2500 }); } handleScan(code); };
  useEffect(() => setScanPageHandler((code) => scanRef.current && scanRef.current(code)), []);
  useEffect(() => setActiveIdx(0), [results]);
  const onSearchKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx((i) => Math.min(i + 1, results.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const v = e.currentTarget.value.trim();
      // Barkod (6+ rakam) okunduysa: listedeki ESKİ/bekleyen sonucu asla ekleme → kodu birebir çöz.
      const isBarcode = /^\d{6,}$/.test(v.replace(/\s/g, ""));
      if (isBarcode) {
        const exact = results.find((rr) => { const pr = rr.product || {}; return [pr.barcode, pr.code, ...(pr.barcodes || [])].some((b) => String(b || "").trim() === v); });
        if (exact) addProduct(exact.product, exact.sale_discount != null ? exact.sale_discount : discOf(exact.product), exact, true);
        else handleScan(v);
      } else if (results.length) { const r = results[Math.min(activeIdx, results.length - 1)] || results[0]; addProduct(r.product, r.sale_discount != null ? r.sale_discount : discOf(r.product), r); }
      else if (v) handleScan(v, false);
    }
  };

  const lineOf = (id) => cart.find((l) => l.product_id === id);
  const setLine = (id, k, v) => pc.dispatch({ op: "set", pid: id, patch: { [k]: v } });
  // İskonto girişi: liste fiyatı varsa net'i iskontodan türet + KDV dahil birim fiyatı tazele; manuel fiyatlı üründe (liste 0) fiyat değişmez
  const setDiscLine = (id, v) => {
    const l = lineOf(id); if (!l) return;
    const lp = Number(l.list_price) || 0;
    if (lp <= 0) return setLine(id, "sale_discount", v);
    const net = r2(lp * (1 - (Number(v) || 0) / 100));
    pc.dispatch({ op: "set", pid: id, patch: { sale_discount: v, unit_net: net, gross: String(r2(net * (1 + (Number(l.kdv_rate) || 0) / 100))), manual_price: false } });
  };
  // Manuel birim fiyat (KDV DAHİL genel satış fiyatı; kdvsiz modda net) doğrudan girilir → net'i türet, liste gerekmez
  const setGrossLine = (id, v) => {
    const l = lineOf(id); if (!l) return;
    const g = Number(v) || 0;
    const net = r2(g / (1 + (Number(l.kdv_rate) || 0) / 100));
    const lp = Number(l.list_price) || 0;
    pc.dispatch({ op: "set", pid: id, patch: { gross: v, unit_net: net, sale_discount: lp > 0 ? Math.max(0, r2((1 - net / lp) * 100)) : 0, manual_price: true } });
  };
  const incQty = (id, d) => { const l = lineOf(id); if (l) setLine(id, "quantity", Math.max(0, r2((Number(l.quantity) || 0) + d))); };
  const rmLine = (id) => pc.dispatch({ op: "remove", pid: id });
  const setQtyLine = (id, v) => setLine(id, "quantity", v);
  const setDrumLine = (id, v) => setLine(id, "drum_id", v);

  // KDV DAHİL birim fiyat (müşterinin gördüğü) ESAS alınır: satır tutarı = gross × adet; net ve KDV bundan türetilir → 20 ₺ × 50 = 1.000,00 (yuvarlama farkı yok)
  const unitGross = (l) => { const g = Number(l.gross); return Number.isFinite(g) && l.gross !== "" ? r2(g) : r2((Number(l.unit_net) || 0) * (1 + (Number(l.kdv_rate) || 0) / 100)); };
  const kdvF = (l) => 1 + (Number(l.kdv_rate) || 0) / 100;
  const unitNet = (l) => r2(unitGross(l) / kdvF(l));
  const lineGross = (l) => r2(unitGross(l) * (Number(l.quantity) || 0));
  const lineNet = (l) => r2(lineGross(l) / kdvF(l));
  const lineKdv = (l) => r2(lineGross(l) - lineNet(l));
  const totals = useMemo(() => {
    const sub = cart.reduce((s, l) => s + lineNet(l), 0);
    const kdv = cart.reduce((s, l) => s + lineKdv(l), 0);
    const grand = cart.reduce((s, l) => s + lineGross(l), 0);
    return { sub: r2(sub), kdv: r2(kdv), grand: r2(grand) };
  }, [cart]); // eslint-disable-line

  const resetUi = () => { setQuery(""); setResults([]); setPayOverride(""); setLeftTab("search"); setShowProfit(false); };
  // Beklet: ekran kapanır, sepet (sunucuda + cihazda) açık kalır → "Sepete Devam" ile geri gelinir
  const parkCart = () => { pc.park(); setOpen(false); resetUi(); };
  // Vazgeç: sepet kapatılır (sahibi/yönetici → herkes için kapanır; ortak sepetteki misafir → yalnız ayrılır)
  const tryClose = async () => {
    if (cart.length) {
      if (pc.isOwner || isAdmin) {
        if (!await confirm(`Sepet kapatılsın mı? ${cart.length} kalem silinecek, satış yapılmayacak.${pc.editors.length > 1 ? "\nSepeti düzenleyen diğer kullanıcılar için de kapanır." : ""}\n\nSepeti saklamak için "Beklet"i kullanın.`)) return;
        if (!await pc.cancelCart()) return;
      } else {
        if (!await confirm(`${pc.owner} kullanıcısının sepetinden ayrılıyorsunuz. Sepet onun ekranında açık kalır.`)) return;
        pc.leave();
      }
    } else {
      await pc.cancelCart();
    }
    setOpen(false); resetUi();
  };
  const startNew = () => {
    const resumed = pc.startFresh();
    resetUi();
    setOpen(true);
    if (resumed && pc.items.length) toast.info(`Kaydedilmiş sepet geri yüklendi · ${pc.items.length} kalem`);
  };
  const joinCart = async (id) => {
    if (pc.cartId && pc.cartId !== id && pc.items.length && !await confirm("Açık sepetinizden ayrılıp bu sepete katılacaksınız. Kendi sepetiniz sunucuda açık kalır (Canlı Sepetler'den geri dönebilirsiniz).")) return;
    const data = await pc.join(id);
    resetUi(); setOpen(true);
    if (data.owner_id !== user?.id) toast.success(`${data.owner_name} kullanıcısının sepetine katıldınız — değişiklikler anlık senkronize`);
  };
  // Açık sepeti beklet (sunucuda açık kalır), yeni boş sepet başlat → aynı anda ikinci bir satış yapılabilir.
  const startBlank = async () => {
    await pc.newBlank();
    resetUi();
    toast.success("Yeni boş sepet açıldı — önceki sepet Canlı Sepetler'de açık kaldı, oradan geri dönebilirsiniz");
  };
  // Ana ekrandan YENİ sepet: mevcut açık sepet varsa Canlı Sepetler'de açık kalır, temiz boş sepet açılır.
  const startNewCart = async () => {
    const had = pc.items.length > 0 || pc.cartId;
    await pc.newBlank();
    resetUi();
    setOpen(true);
    if (had) toast.success("Yeni boş sepet açıldı — önceki sepet Canlı Sepetler'de açık kaldı");
  };

  const findContact = (t) => (contacts || []).find((c) => (c.title || "").trim().toLowerCase() === (t || "").trim().toLowerCase());
  // KDV'siz mod yalnız KDV'siz (vergiden muaf) ELEKTRİKÇİ carisi seçiliyken açılır — cari yokken geçilemez
  const selContact = findContact(title);
  const kdvsizAllowed = isAdmin && !!selContact && selContact.type === "elektrikci" && !!selContact.vat_exempt;
  const modes = MODES.filter(([v]) => v !== "kdvsiz" || kdvsizAllowed);
  useEffect(() => {
    if (!isAdmin || !(contacts && contacts.length) || mode !== "kdvsiz" || kdvsizAllowed) return;
    pc.setMode(selContact && selContact.type === "elektrikci" ? "faturali" : "genel");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, kdvsizAllowed, isAdmin, contacts && contacts.length]);

  const askPay = () => {
    if (workerLocked) return toast.error(restricted ? "Yönetici sepette — satışı yönetici tamamlayacak" : "KDV'siz satışı yalnız yönetici tamamlayabilir");
    if (cart.filter((l) => (Number(l.quantity) || 0) > 0).length === 0) return toast.error("En az bir ürün ekleyin");
    if (payMethod === "veresiye" && !String(title || "").trim()) pc.setPayMethod("nakit");   // cari kaldırıldıysa veresiye kalamaz
    setPayOverride(""); setPayOpen(true);
  };

  const mapLine = (l) => ({
    product_id: l.product_id, name: l.name, code: l.code, barcode: l.barcode, image_url: l.image_url,
    quantity: Number(l.quantity), list_price: Number(l.list_price) || 0,
    sale_discount: Number(l.sale_discount) || 0, unit_net: unitNet(l), unit_gross: unitGross(l), kdv_rate: Number(l.kdv_rate) || 0,
    unit_cost: Number(l.cost) || 0, cost: l.cost, drum_id: l.drum_id || undefined,
  });
  const commit = async (parts, cashReceived) => {
    const payParts = Array.isArray(parts) && parts.length ? parts : null;
    const ovNum = Number(payOverride);
    const override_total = payOverride !== "" && !Number.isNaN(ovNum) && ovNum > 0 ? r2(ovNum) : undefined;
    setSaving(true);
    try {
      const res = await pc.complete({ payMethod, overrideTotal: override_total, mapLine, payments: payParts, cashReceived });
      if (res.queued) {
        toast.warning("Bağlantı yok — satış kuyruğa alındı. Bağlantı gelince otomatik gönderilecek (çift kayıt olmaz).", { duration: 8000 });
      } else if (res.elsewhere) {
        toast.info(res.message || "Bu sepet başka bir kullanıcı tarafından tamamlandı.");
      } else {
        const data = res.sale;
        const dNote = data.override_applied && data.manual_discount ? ` · iskonto ${TRY(data.manual_discount)}` : "";
        const payNote = data.payment_method === "karma" ? `Parçalı: ${paymentsSummary(data.payments)}` : PAY_LABEL[data.payment_method] || PAY_LABEL[payMethod];
        const cNote = data.moved_to_cari ? (data.payment_method === "veresiye" ? ` · ${data.cari_contact_title} defterine VERESİYE yazıldı` : Number(data.veresiye_amount) > 0 ? ` · ${data.cari_contact_title} defterine işlendi, ${TRY(data.veresiye_amount)} veresiye açık` : ` · ${data.cari_contact_title} defterine satış + tahsilat işlendi`) : "";
        toast.success(`Satış tamamlandı · ${TRY(data.grand_total)}${dNote} · ${payNote}${cNote} · stoktan düşüldü`);
      }
      setPayOpen(false); setOpen(false); resetUi();
      loadHistory();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  // ---- Sepetten fatura: sepet açık kalır; fatura QNB'den kesilince satış FATURA TUTARIYLA tamamlanır (ödeme faturaya işlenir)
  const payable = (() => { const ov = Number(payOverride); return payOverride !== "" && !Number.isNaN(ov) && ov > 0 ? r2(ov) : totals.grand; })();
  const askInvoice = () => {
    if (workerLocked) return toast.error(restricted ? "Yönetici sepette — satışı yönetici tamamlayacak" : "KDV'siz satışa fatura kesilemez");
    if (!cart.some((l) => (Number(l.quantity) || 0) > 0)) return toast.error("En az bir ürün ekleyin");
    if (!pc.cartId) return toast.error("Sepet henüz sunucuya kaydedilmedi — bağlantıyı kontrol edip tekrar deneyin");
    setPayOpen(false); setCartInvOpen(true);
  };
  const invoiceAllowed = isAdmin && mode !== "kdvsiz";
  // Fatura ekranından geri dönülünce ödeme ekranı (pazarlık tutarı korunarak) yeniden açılır
  const onCartInvOpenChange = (o) => { setCartInvOpen(o); if (!o && open) setPayOpen(true); };
  const completeWithInvoice = async (r) => {
    setSaving(true);
    try {
      const res = await pc.complete({ payMethod: r.method, overrideTotal: r.total, mapLine, payments: null, invoiceId: r.invoiceId });
      if (res.queued) toast.warning("Bağlantı yok — fatura kesildi, satış kuyruğa alındı; bağlantı gelince otomatik tamamlanır (çift kayıt olmaz).", { duration: 8000 });
      else if (res.elsewhere) toast.info(res.message || "Bu sepet başka bir kullanıcı tarafından tamamlandı.");
      else toast.success(`Fatura ${r.invoiceNo || ""} kesildi · satış tamamlandı · ${TRY(res.sale.grand_total)} · ${r.method === "veresiye" ? "ödeme alınmadı (cari hesaba borç)" : `${PAY_LABEL[r.method] || r.method} tahsilatı faturaya işlendi`} · stoktan düşüldü`, { duration: 8000 });
      cartInvRef.current = null; setCartInvPending(null); setCartInvOpen(false); setOpen(false); resetUi();
      loadHistory(); loadContacts();
    } catch (e) {
      setCartInvPending(r);
      toast.error(`Fatura kesildi ancak satış tamamlanamadı: ${apiError(e)} — 'Satışı Tamamla' ile tekrar deneyin`, { duration: 10000 });
    } finally { setSaving(false); }
  };
  const onInvoiceDraft = ({ invoice, contact, method, total, alreadySent }) => {
    const r = { invoiceId: invoice.id, invoiceNo: invoice.invoice_no || invoice.qnb?.invoice_no || "", method, total };
    cartInvRef.current = r;
    if (contact) setQnbExtra([contact]);
    if (alreadySent) { completeWithInvoice(r); return; }
    setQnbInv(invoice);
  };
  const onQnbSent = (sent) => {
    const r = cartInvRef.current;
    if (!r || r.invoiceId !== sent.id) return;
    completeWithInvoice({ ...r, invoiceNo: sent.invoice_no || r.invoiceNo });
  };

  const cancelSale = async (s) => {
    if (!await confirm(`Bu hızlı satışı iptal etmek istediğinize emin misiniz?\n${s.item_count} kalem stoğa geri iade edilecek. Bu işlem geri alınamaz.`)) return;
    try {
      const { data } = await api.post(`/quick-sales/${s.id}/cancel`);
      toast.success(`Satış iptal edildi · ${data.restored} ürün stoğa iade edildi`);
      loadHistory();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteSale = async (s) => {
    if (!await confirm(`Bu hızlı satışı KALICI olarak silmek istediğinize emin misiniz?\nFiş No: ${receiptBelgeNo(s)} · ${s.item_count} kalem\nBu işlem geri alınamaz${!s.cancelled ? " ve ürünler stoğa iade edilir" : ""}.`)) return;
    try {
      const { data } = await api.delete(`/quick-sales/${s.id}`);
      toast.success(`Satış kalıcı olarak silindi${data.restored ? ` · ${data.restored} ürün stoğa iade edildi` : ""}`);
      loadHistory();
    } catch (e) { toast.error(apiError(e)); }
  };

  // Fişi yeniden sepete al: fiş iptal (stok geri) + aynı kalem/cari/mod ile yeni canlı sepet → terminal açılır
  const openCartFrom = async (cartDoc) => {
    if (pc.cartId && pc.cartId !== cartDoc.id && pc.items.length) toast.info("Açık sepetiniz Canlı Sepetler'de saklandı");
    await pc.join(cartDoc.id);
    resetUi(); setOpen(true);
  };
  const reopenSale = async (s) => {
    const msg = s.cancelled
      ? `${receiptBelgeNo(s)} kalemleriyle yeni canlı sepet açılsın mı?`
      : `${receiptBelgeNo(s)} İPTAL edilecek (${s.item_count} kalem stoğa iade) ve aynı kalemler/cari/mod ile yeni canlı sepet açılacak.\nDüzenleyip yeniden satış yapabilirsiniz. Devam edilsin mi?`;
    if (!await confirm(msg)) return;
    try {
      const { data } = await api.post(`/quick-sales/${s.id}/reopen`);
      toast.success(s.cancelled ? "Kalemler yeni sepete alındı" : "Fiş iptal edildi · stok geri yüklendi · sepet yeniden açıldı");
      loadHistory();
      await openCartFrom(data.cart);
    } catch (e) { toast.error(apiError(e)); }
  };
  const duplicateSale = async (s) => {
    try {
      const { data } = await api.post(`/quick-sales/${s.id}/duplicate-cart`);
      toast.success(`${receiptBelgeNo(s)} kalemleri yeni sepete kopyalandı · fiş korunuyor`);
      await openCartFrom(data.cart);
    } catch (e) { toast.error(apiError(e)); }
  };

  const todayStats = useMemo(() => {
    const t = new Date().toISOString().slice(0, 10);
    const today = history.filter((s) => (s.created_at || "").slice(0, 10) === t && !s.cancelled);
    const total = r2(today.reduce((s, x) => s + (x.grand_total || 0), 0));
    return { count: today.length, total, avg: today.length ? r2(total / today.length) : 0 };
  }, [history]);

  const profitData = useMemo(() => {
    const cost = cart.reduce((s, l) => s + (Number(l.cost) || 0) * (Number(l.quantity) || 0), 0);
    const rev = totals.sub;
    const profit = r2(rev - cost);
    const margin = rev > 0 ? r2((profit / rev) * 100) : 0;
    return { cost: r2(cost), rev, profit, margin };
  }, [cart, totals]);

  // Cari seçilince ünvanı yaz + elektrikçi cariyse satış modunu otomatik uygula (faturalı / KDV'siz); cari kalkarsa KDV'siz mod kapanır
  const onSelectContact = (v) => {
    const c = findContact(v);
    const nextMode = c && c.type === "elektrikci" ? (c.vat_exempt && isAdmin ? "kdvsiz" : "faturali") : (mode === "kdvsiz" ? "genel" : undefined);
    pc.setTitle(v, nextMode);
    const t = (v || "").trim().toLowerCase();
    if (t && history.some((s) => !s.cancelled && (s.title || "").trim().toLowerCase() === t)) setLeftTab("cari");
    else setLeftTab("search");
    if (!t && payMethod === "veresiye") pc.setPayMethod("nakit");
  };
  // Seçili cariye önceki hızlı satışlar — satış bazlı ayrıntılı (iptal edilenler hariç); kısıtlı modda (yönetici sepette) fiyat sızmasın → gizli
  const cariSales = useMemo(() => {
    const t = (title || "").trim().toLowerCase();
    if (!t || restricted) return [];
    return (history || []).filter((s) => !s.cancelled && (s.title || "").trim().toLowerCase() === t);
  }, [title, history, restricted]);
  const cariSummary = useMemo(() => quickSaleSummary(cariSales), [cariSales]);
  // Geçmiş satırından ürünü önceki (KDV dahil) fiyatıyla sepete ekle
  const addFromHistoryItem = (it) => {
    const pid = it.product_id || `n:${(it.name || "").toLowerCase()}`;
    const kdv = Number(it.kdv_rate) || 0;
    const gross = unitGrossOf(it);
    const net = r2(gross / (1 + kdv / 100));
    if (cart.some((l) => l.product_id === pid)) pc.dispatch({ op: "remove", pid });
    pc.dispatch({ op: "add", item: {
      product_id: pid, name: it.name, code: it.code || "", barcode: it.barcode || "",
      image_url: it.image_url || "", stock: 0, unit: it.unit || "ADET", brand: it.brand || "",
      quantity: 1, list_price: Number(it.list_price) || 0, unit_net: net, gross: String(gross),
      manual_price: true, sale_discount: Number(it.sale_discount) || 0, kdv_rate: kdv,
      cost: Number(it.unit_cost) || 0, cost_source: "invoice", pos: null, is_cable: false, drums: [], drum_id: "",
    } });
    toast.success(`'${it.name}' önceki fiyattan sepete eklendi`);
  };

  const contactOptions = useMemo(() => contactOptionsOf(contacts || []), [contacts]);
  const qnbContacts = useMemo(() => [...qnbExtra, ...(contacts || []).filter((c) => !qnbExtra.some((x) => x.id === c.id))], [qnbExtra, contacts]);
  const hasSearch = !!(query.trim() || filters.brand || filters.series || filters.category);
  const openCariTab = () => { setLeftTab("cari"); setQuery(""); setResults([]); };

  const searchCtx = { query, setQuery, runSearch, results, searching, facets, filters, setFilter, clearFilters, activeIdx, setActiveIdx, onSearchKey, addProduct, discOf,
    onScan: () => setScanOpen(true), onNewProduct: setNewProd, hasSearch, leftTab, setLeftTab, cariSales, cariSummary, addFromHistoryItem, viewInvoice };
  const cartCtx = { cart, mode, modes, changeMode, kdvsizAllowed, title, onSelectContact, contactOptions, selContact, cariSummary, openCariTab, unitNet, lineGross,
    handlers: { setQtyLine, incQty, setDiscLine, setGrossLine, setDrumLine, rmLine }, totals, profitData, showProfit, setShowProfit, note, setNote: (v) => pc.setNote(v), askPay, saving, tryClose, parkCart };
  const historyActions = { onPreview: async (s) => setPreview(await fetchSale(s)), onEdit: setEdit, onToCari: setCari, onInvoice: async (s) => setInvoiceSale(await fetchSale(s)),
    onReturn: async (s) => setRet(await fetchSale(s)), onHistory: setHistSale, onCancel: cancelSale, onDelete: deleteSale, onReopen: reopenSale, onDuplicate: duplicateSale, onChangeMode: setModeSale,
    onPrep: async (s) => setPrepSale(await fetchSale(s)), onQuote: async (s) => setQuoteSale(await fetchSale(s)) };
  const hasCart = !open && (cart.length > 0 || pc.cartId);

  return (
    <div className="space-y-5 stagger-in" data-testid="quick-sale-page">
      {/* ============ HERO / PORTAL GİRİŞİ ============ */}
      <div className="relative overflow-hidden rounded-3xl bg-[#0B192C] text-white shadow-xl" data-testid="quick-sale-header">
        <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-[#EA5820]/25 blur-3xl" />
        <div className="pointer-events-none absolute left-1/3 -bottom-24 h-56 w-56 rounded-full bg-[#1A3557] blur-3xl opacity-70" />
        <div className="relative grid lg:grid-cols-[minmax(0,1fr)_auto] gap-6 p-6 md:p-8">
          <div className="min-w-0">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-300"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />POS Aktif</span>
            <h1 className="mt-3 font-head text-3xl md:text-4xl lg:text-5xl font-black tracking-tight">Hızlı Satış Terminali</h1>
            <p className="text-sm text-white/60 mt-2 max-w-xl leading-relaxed">{isAdmin ? "Barkod okut · ürünü hızlıca seç · iskonto/pazarlık uygula. Satış tamamlanınca stok anında düşer." : "Barkod okut · ürünü hızlıca seç · Genel Satış veya Elektrikçi Faturalı satış yap ya da açık bir sepete katıl."}</p>
            <div className="mt-5 flex flex-wrap gap-2 text-[11px] text-white/55">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1"><ScanLine className="h-3.5 w-3.5 text-[#FF8A4C]" />Barkod okuyucu sayfa açıkken doğrudan sepete ekler</span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1"><UsersIcon className="h-3.5 w-3.5 text-[#FF8A4C]" />Sepetler canlı · birlikte düzenlenir</span>
            </div>
          </div>
          <div className="flex flex-col gap-2.5 lg:min-w-[300px] lg:justify-center">
            {hasCart ? (
              <>
                <Button size="lg" onClick={startNew} data-testid="qs-resume-cart-btn" className="h-16 rounded-2xl bg-[#EA5820] hover:bg-[#D84A15] text-white font-bold shadow-lg shadow-[#EA5820]/30 active:scale-[0.98] transition-transform justify-between px-5">
                  <span className="inline-flex items-center gap-2.5"><ShoppingCart className="h-5 w-5" /><span className="text-left leading-tight"><span className="block text-base">Sepete Devam</span><span className="block text-[11px] font-semibold text-white/80">{cart.length} kalem · {TRY(totals.grand)}</span></span></span><ArrowRight className="h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" onClick={startNewCart} data-testid="new-quick-sale-btn" className="h-12 rounded-2xl bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white font-bold active:scale-[0.98] transition-transform"><Plus className="h-5 w-5 mr-2" />Yeni Sepet Aç</Button>
              </>
            ) : (
              <Button size="lg" onClick={startNew} data-testid="new-quick-sale-btn" className="h-16 rounded-2xl bg-[#EA5820] hover:bg-[#D84A15] text-white text-lg font-bold shadow-lg shadow-[#EA5820]/30 active:scale-[0.98] transition-transform px-8"><Zap className="h-5 w-5 mr-2" />Yeni Satış Başlat</Button>
            )}
            {isAdmin && <Button size="lg" variant="outline" onClick={() => setZOpen(true)} data-testid="qs-zreport-btn" className="h-12 rounded-2xl bg-white/5 border-white/15 text-white hover:bg-white/15 hover:text-white font-bold active:scale-[0.98] transition-transform"><Wallet className="h-5 w-5 mr-2" />Gün Sonu (Z Raporu)</Button>}
          </div>
        </div>
        {isAdmin && (
          <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-3 px-6 md:px-8 pb-6 md:pb-8">
            <HeroKpi label="Bugünkü Satış" value={NUM(todayStats.count)} sub="fiş adedi" icon={ShoppingCart} testid="qs-kpi-today-count" />
            <HeroKpi label="Bugünkü Ciro" value={TRY(todayStats.total)} sub="KDV dahil" icon={Zap} accent testid="qs-kpi-today-total" />
            <HeroKpi label="Ortalama Fiş" value={TRY(todayStats.avg)} sub="fiş başına" icon={Sigma} testid="qs-kpi-today-avg" />
            <HeroKpi label="Toplam Kayıt" value={NUM(history.length)} sub="tüm zamanlar" icon={Boxes} testid="qs-kpi-total" />
          </div>
        )}
      </div>

      <PendingSalesBar pending={pc.pending} onRetry={() => pc.flushPending()} onRestore={(k) => { if (pc.restorePending(k)) { toast.success("Satış sepete geri yüklendi"); startNew(); } }} onRemove={async (k) => { if (await confirm("Bekleyen satış kuyruktan silinsin mi? (Satış yapılmayacak)")) pc.removePending(k); }} />
      <LiveCartsPanel user={user} currentCartId={pc.cartId} onJoin={joinCart} />

      {/* Personel özeti + fiş/satış geçmişi YALNIZ yönetici — depo müdürü yapılan satışları göremez */}
      {isAdmin && <StaffTodayCard rows={staffToday} />}
      {isAdmin && <SalesHistoryPanel history={history} contactOptions={contactOptions} onNew={startNew} actions={historyActions} />}

      {/* ============ TAM EKRAN POS TERMİNALİ ============ */}
      <PosTerminal open={open} onPark={parkCart} onNewCart={startBlank} pc={pc} user={user} isAdmin={isAdmin} restricted={restricted} workerLocked={workerLocked} search={searchCtx} cart={cartCtx} />

      <PayDialog open={payOpen} onOpenChange={setPayOpen} totals={totals} cartCount={cart.length} payOverride={payOverride} setPayOverride={setPayOverride}
        payMethod={payMethod} setPayMethod={(v) => pc.setPayMethod(v)} onConfirm={commit} saving={saving} cariTitle={title} onInvoice={invoiceAllowed ? askInvoice : null} />
      <EditSaleDialog sale={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); loadHistory(); }} />
      <ChangeModeDialog sale={modeSale} onClose={() => setModeSale(null)} onDone={() => { setModeSale(null); loadHistory(); }} />
      <CariTransferDialog sale={cari} isAdmin={isAdmin} contactOptions={contactOptions} onClose={() => setCari(null)} onDone={() => { setCari(null); loadHistory(); }} />
      <NewProductDialog initial={newProd} onClose={() => setNewProd(null)} onCreated={(p, disc, r) => { addProduct(p, disc, r); setNewProd(null); }} />
      <ReceiptPreviewDialog sale={preview} isAdmin={isAdmin} onClose={() => setPreview(null)} onSendPrep={(s) => { setPreview(null); setPrepSale(s); }} onToQuote={isAdmin ? (s) => { setPreview(null); setQuoteSale(s); } : undefined} />
      <ConvertToQuoteDialog sale={quoteSale} onClose={() => setQuoteSale(null)} />
      <SendToPrepDialog sale={prepSale} onClose={() => setPrepSale(null)} onSent={() => { setPrepSale(null); loadHistory(); }} />
      <InvoiceFromSaleDialog sale={invoiceSale} contacts={contacts} contactOptions={contactOptions} onClose={() => setInvoiceSale(null)}
        onCreated={(data) => { setInvoiceSale(null); setQnbInv(data); loadHistory(); loadContacts(); }} />
      <QnbInvoiceDialog invoice={qnbInv} contacts={qnbContacts} onClose={() => setQnbInv(null)} onDone={loadHistory} onSent={onQnbSent} />
      <CartInvoiceDialog open={cartInvOpen} onOpenChange={onCartInvOpenChange} cartId={pc.cartId} cart={cart} unitNet={unitNet} payable={payable} grand={totals.grand}
        onPayableSync={(v) => setPayOverride(Math.abs(v - totals.grand) < 0.005 ? "" : String(v))}
        contacts={qnbContacts} selContact={selContact} cartTitle={title} payMethod={payMethod} pending={cartInvPending} saving={saving}
        onDraft={onInvoiceDraft} onRetry={() => cartInvPending && completeWithInvoice(cartInvPending)} />
      <InvoiceDetailDialog invoice={detailInv} contacts={contacts} onClose={() => setDetailInv(null)} onQnb={(i) => { setDetailInv(null); setQnbInv(i); }} />
      <ZReportDialog open={zOpen} onOpenChange={setZOpen} />
      <ReturnDialog sale={ret} onClose={() => setRet(null)} onDone={() => { setRet(null); loadHistory(); }} />

      {/* ============ CHANGE HISTORY DIALOG ============ */}
      <Dialog open={!!histSale} onOpenChange={(o) => { if (!o) setHistSale(null); }}>
        <DialogContent className="max-w-[96vw] sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="qs-change-history-dialog">
          <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><History className="h-5 w-5 text-primary" />Değişiklik Geçmişi</DialogTitle></DialogHeader>
          {histSale && <RecordHistory entityType="quick_sale" entityId={histSale.id} />}
        </DialogContent>
      </Dialog>

      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={handleScan} />
    </div>
  );
}
